import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { BottomSheet, BottomSheetContentLayout, IconButton, Menu, useOverlayContext } from 'seagull';
import { ChevronRight, DocumentCheckmark, Invoice, Plus, PlusCircle, UserAdd } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { NewServiceType, SelectNewServiceTypeModal } from '~/components/AddServicesButton/SelectNewServiceTypeModal/SelectNewServiceTypeModal';
import { TemplateVariablesProvider } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { ManageContactModalForVendor } from '~/components/ManageContactModal/ManageContactModalForVendor';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import { useStore } from '~/contexts';
import { useActiveServiceTemplates } from '~/hooks/reactQuery/queries/useActiveServiceTemplates';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGlobalBillClient } from '~/hooks/useGlobalBillClient';
import { useOpenNewProposal } from '~/hooks/useOpenNewProposal';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { withDeviceProvider } from '~/providers/DeviceProvider';
import { selectors } from '~/store/state-selectors';
export var ItemId = /*#__PURE__*/ function(ItemId) {
    ItemId["CreateClient"] = "create-client";
    ItemId["BillClient"] = "bill-client";
    ItemId["CreateContact"] = "create-contact";
    ItemId["CreateProposal"] = "create-proposal";
    ItemId["CreateService"] = "create-service";
    return ItemId;
}({});
const EMPTY_TEXT_FIELDS = [];
const EMPTY_RICH_TEXT_FIELDS = [];
// WHY: the mobile New-service builder is portal-mounted outside the studio provider, so seed one here or custom-field Create has nowhere to write.
function CustomFieldsPickerScope(param) {
    let { worksAt, children } = param;
    const { CUSTOM_VARIABLES } = useFeatureFlags();
    if (!CUSTOM_VARIABLES || !worksAt) return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
    return /*#__PURE__*/ _jsx(TemplateVariablesProvider, {
        contact: null,
        worksAt: worksAt,
        agreementName: null,
        effectiveDate: null,
        initialTemplateVariableValues: undefined,
        textFields: EMPTY_TEXT_FIELDS,
        richTextFields: EMPTY_RICH_TEXT_FIELDS,
        children: children
    });
}
export const getQuickActionsMenuTID = ()=>getTestIds();
function ActualQuickActionsMenu() {
    const { QuickActionsMenuTID } = useMemo(()=>getQuickActionsMenuTID(), []);
    const { refresh: refreshContacts } = useContacts({
        refreshOnMount: true
    });
    const { showModal, startLoader, stopLoader, showErrorToast, showSuccessToast } = useOverlayContext();
    const { isLoadingProposalTemplates, openNewProposal } = useOpenNewProposal();
    const openCreateClientModal = useOpenCreateClientModal();
    const { FE_CRM } = useFeatureFlags();
    const worksAt = useStore(selectors.user.business);
    const { openGlobalBillModal } = useGlobalBillClient();
    const serviceTemplateApi = useServiceTemplateApi();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: quickActionsMenuTexts } = useLocaleKeys('components.quickActionsMenu');
    const { data: serviceTemplates, refresh: refreshServiceTemplates } = useActiveServiceTemplates({
        refreshOnMount: true
    });
    async function createNewService(closeMenu) {
        const newServiceType = await showModal({
            biName: 'select_new_service_type',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(SelectNewServiceTypeModal, _object_spread({}, p))
        });
        if (!newServiceType) return;
        closeMenu();
        showModal({
            biName: 'service_builder',
            closeOnEscapeKey: true,
            content: (p)=>{
                var _ref;
                return /*#__PURE__*/ _jsx(CustomFieldsPickerScope, {
                    worksAt: worksAt,
                    children: /*#__PURE__*/ _jsx(ServiceBuilder, _object_spread({
                        serviceOrigin: "template",
                        isCreatingBundle: newServiceType === NewServiceType.Bundle,
                        serviceNameBlacklist: (_ref = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.map((s)=>s.name)) !== null && _ref !== void 0 ? _ref : [],
                        onSave: async (output)=>{
                            const body = serviceTemplateModel.fromService(output);
                            const loaderName = 'createServiceTemplateLoader';
                            startLoader(loaderName);
                            const result = await handlePromise(serviceTemplateApi.create(body));
                            if (result.success) {
                                await refreshServiceTemplates({
                                    throwOnError: false
                                });
                                showSuccessToast(commonTexts.toasts.serviceCreated(), 'service-template-created-toast');
                            } else {
                                showErrorToast(commonTexts.toasts.failedToCreateService(), 'create-service-fail-toast');
                            }
                            stopLoader(loaderName);
                            return result;
                        }
                    }, p))
                });
            }
        });
    }
    async function createContact(closeMenu) {
        const savedContact = await showModal({
            biName: 'quick_actions_create_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BottomSheet, {
                    open: true,
                    joinOpenSheetStack: true,
                    onClose: dismissModal,
                    children: /*#__PURE__*/ _jsx(ManageContactModalForVendor, {
                        testId: "manage-contact-modal",
                        onClose: (saved)=>dismissModal(saved),
                        origin: "Quick actions menu"
                    })
                });
            }
        });
        refreshContacts();
        if (savedContact) {
            closeMenu();
        }
    }
    const buildItems = (closeMenu)=>[
            {
                id: "bill-client",
                body: quickActionsMenuTexts.billClient(),
                prefixIcon: /*#__PURE__*/ _jsx(Invoice, {}),
                bi: {
                    name: 'quick_actions_bill_client'
                },
                onClick: ()=>{
                    closeMenu();
                    openGlobalBillModal();
                }
            },
            ...FE_CRM ? [
                {
                    id: "create-client",
                    body: quickActionsMenuTexts.createClient(),
                    prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
                    bi: {
                        name: 'quick_actions_create_client'
                    },
                    onClick: openCreateClientModal
                }
            ] : [],
            ...FE_CRM ? [] : [
                {
                    id: "create-contact",
                    body: quickActionsMenuTexts.createContact(),
                    prefixIcon: /*#__PURE__*/ _jsx(UserAdd, {}),
                    bi: {
                        name: 'quick_actions_create_contact'
                    },
                    onClick: ()=>createContact(closeMenu)
                }
            ],
            {
                id: "create-proposal",
                body: quickActionsMenuTexts.createProposal(),
                disabled: isLoadingProposalTemplates,
                prefixIcon: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                bi: {
                    name: 'quick_actions_create_proposal'
                },
                onClick: ()=>openNewProposal()
            },
            {
                id: "create-service",
                body: quickActionsMenuTexts.createService(),
                prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                bi: {
                    name: 'quick_actions_create_service'
                },
                onClick: ()=>createNewService(closeMenu)
            }
        ].map((item)=>_object_spread_props(_object_spread({}, item), {
                testId: QuickActionsMenuTID.Item(item.id),
                suffixIcon: /*#__PURE__*/ _jsx(ChevronRight, {})
            }));
    const openMenu = ()=>showModal({
            biName: 'quick_actions_menu',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BottomSheet, {
                    open: true,
                    joinOpenSheetStack: true,
                    onClose: dismissModal,
                    children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: {
                            title: quickActionsMenuTexts.quickActions()
                        },
                        children: /*#__PURE__*/ _jsx(Menu, {
                            dimensions: {
                                width: 'auto'
                            },
                            items: buildItems(dismissModal)
                        })
                    })
                });
            }
        });
    return /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'open_quick_actions_menu'
        },
        skin: "primaryBoxed",
        size: "smallMedium",
        testId: QuickActionsMenuTID.Button(),
        onClick: openMenu,
        children: /*#__PURE__*/ _jsx(Plus, {
            size: 16
        })
    });
}
export const QuickActionsMenu = withDeviceProvider(ActualQuickActionsMenu);
